import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { Widget } from '../../components/dashboard/Widget';
import { PageHeader } from '../../components/ui/PageHeader';

interface Mon {
  transmissions: Array<{
    transmission_type: string;
    last_at: string | null;
    failed_recent: number;
  }>;
  overdueDrafts: number;
  rpHealth: { ok: boolean; latencyMs: number; statusCode?: number };
}

/** Screen 66 — E-invoice monitoring. RP health, overdue drafts, per-channel last batch. */
export default function EInvoiceMonitoring() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [m, setM] = useState<Mon | null>(null);
  useEffect(() => {
    apiClient
      .get<{ data: Mon }>('/einvoice/monitoring')
      .then((r) => setM(r.data.data))
      .catch(showApiError);
  }, [showApiError]);
  if (!m) return null;
  return (
    <div className="space-y-4">
      <PageHeader title={t('einvoice.monitoring.title')} />
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Widget
          title={t('einvoice.monitoring.rpHealth')}
          value={m.rpHealth.ok ? 'UP' : 'DOWN'}
          tone={m.rpHealth.ok ? 'default' : 'critical'}
          detail={t('einvoice.monitoring.rpDetail', {
            ms: m.rpHealth.latencyMs,
            status: m.rpHealth.statusCode ?? '—',
          })}
        />
        <Widget
          title={t('einvoice.monitoring.overdueDrafts')}
          value={m.overdueDrafts}
          tone={m.overdueDrafts > 0 ? 'critical' : 'default'}
          detail={t('einvoice.monitoring.overdueDetail')}
        />
        <Widget
          title={t('einvoice.monitoring.channelsRecent')}
          value={m.transmissions.length}
          detail={t('einvoice.monitoring.channelsDetail')}
        />
      </div>
      <section className="border rounded p-4 bg-card">
        <h2 className="font-semibold mb-2">{t('einvoice.monitoring.lastTransmissions')}</h2>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left py-1">{t('einvoice.monitoring.channel')}</th>
              <th>{t('einvoice.monitoring.lastBatch')}</th>
              <th>{t('einvoice.monitoring.failed30d')}</th>
            </tr>
          </thead>
          <tbody>
            {m.transmissions.map((t) => (
              <tr key={t.transmission_type} className="border-t">
                <td className="py-1">
                  <div className="truncate max-w-[18rem]" title={t.transmission_type}>
                    {t.transmission_type}
                  </div>
                </td>
                <td>{t.last_at ?? '—'}</td>
                <td className={t.failed_recent > 0 ? 'text-destructive' : ''}>{t.failed_recent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
